import * as React from "react";
import { Loader2 } from "lucide-react";
import { useGetFamilySourceGridRowsQuery, type SnapshotGridField } from "@/modules/snapshots/businessReports.api";
import type { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import type { SnapshotGridColumnWidths } from "./layout";
import { SnapshotGridTableFixedRow } from "./rowFixed";
import { SnapshotGridTableScrollableRow } from "./rowScrollable";

const SOURCE_PAGE_SIZE = 25;
const NO_RESULT_EVENTS: ReadonlySet<string> = new Set();

interface Props {
  collectionId: string;
  familyOutcomeId: string;
  snapshotCollection: SnapshotCollection;
  headers: string[];
  noteHeaders: string[];
  dataColumns?: SnapshotGridField[];
  hasAddNoteColumn: boolean;
  showSelectionColumn: boolean;
  showActionsColumn: boolean;
  columnWidths?: SnapshotGridColumnWidths;
  colSpan: number;
}

/** Source snapshots stay outside the collection's selection, filters and analysis scope. */
export const FamilySourceRows: React.FC<Props> = ({
  collectionId,
  familyOutcomeId,
  snapshotCollection,
  headers,
  noteHeaders,
  dataColumns,
  hasAddNoteColumn,
  showSelectionColumn,
  showActionsColumn,
  columnWidths,
  colSpan,
}) => {
  const [offset, setOffset] = React.useState(0);
  const { data, isFetching, isError, refetch } = useGetFamilySourceGridRowsQuery({
    snapshotCollectionId: collectionId,
    familyOutcomeId,
    offset,
    limit: SOURCE_PAGE_SIZE,
  });

  React.useEffect(() => {
    if (isFetching || isError || !data || data.offset !== offset || offset * SOURCE_PAGE_SIZE < data.total) return;
    setOffset(Math.max(0, Math.ceil(data.total / SOURCE_PAGE_SIZE) - 1));
  }, [data, isError, isFetching, offset]);

  // Show only the currently authorized response. A refetch hides cached rows until it completes.
  const sourceRows = !isFetching && !isError && data?.offset === offset ? data.results : [];
  const total = data?.total ?? 0;

  return (
    <>
      {sourceRows.map(source => (
        <tr key={source.id} data-row-kind="source_snapshot" className="group h-[53px] bg-slate-50">
          <SnapshotGridTableFixedRow
            businessReportOutcome={source}
            rowNumber={0}
            readOnly
            isSourceRow
            showSelectionColumn={showSelectionColumn}
            showActionsColumn={showActionsColumn}
            columnWidths={columnWidths}
          />
          <SnapshotGridTableScrollableRow
            businessReportOutcome={source}
            snapshotCollection={snapshotCollection}
            promptHeaders={headers}
            noteHeaders={noteHeaders}
            dataColumns={dataColumns}
            businessReportOutcomeResultWithEvent={NO_RESULT_EVENTS}
            hasAddNoteColumn={hasAddNoteColumn}
            columnWidths={columnWidths}
            readOnly
            allowReadOnlyResultDetails
          />
        </tr>
      ))}
      {isFetching || isError || (data && sourceRows.length === 0) || total > SOURCE_PAGE_SIZE ? (
        <tr className="bg-slate-50">
          <td colSpan={colSpan} className="px-6 py-3 text-sm text-slate-600" aria-live="polite">
            {isFetching ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                Loading source snapshots
              </span>
            ) : isError ? (
              <button type="button" className="text-coral hover:underline" onClick={() => void refetch()}>
                Unable to load source snapshots. Retry
              </button>
            ) : sourceRows.length === 0 ? (
              "No accessible source snapshots are available."
            ) : (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="font-medium text-coral hover:underline disabled:text-gray-400 disabled:no-underline"
                  disabled={offset === 0}
                  onClick={() => setOffset(value => value - 1)}
                >
                  Previous sources
                </button>
                <span>
                  Page {offset + 1} of {Math.ceil(total / SOURCE_PAGE_SIZE)}
                </span>
                <button
                  type="button"
                  className="font-medium text-coral hover:underline disabled:text-gray-400 disabled:no-underline"
                  disabled={(offset + 1) * SOURCE_PAGE_SIZE >= total}
                  onClick={() => setOffset(value => value + 1)}
                >
                  Next sources
                </button>
              </div>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
};
